import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import { linkWithUrl } from '@eneco/cms-types';
import { Box, Grid, Heading, NavLink, Stack } from '@eneco/sparky';

import type { FooterMenuContent } from './footer-menu-content.types';

/**
 * `FooterMenu` block for the CMS-agnostic lane, serving **both** providers. Mirrors the legacy component
 * (`libs/sitecore/components/content/src/Footer/FooterMenu.tsx`): a responsive grid of link columns,
 * each with an optional heading, dropping any column that resolves to no links.
 *
 * See `footer-menu-content.types.ts` for the (not yet live-confirmed) content shape and the Sitecore-lane
 * fallback (`items`) — that lane's adapter never nests content under `siteWideContent`.
 */
const FooterMenu: FC = () => {
  const content = useContent<FooterMenuContent>();
  const groups = content.siteWideContent?.items
    ? content.siteWideContent.items.map((group, groupIndex) => ({
        key: group._key ?? String(groupIndex),
        heading: group.heading,
        items: (group.items ?? []).map((navItem, itemIndex) => ({
          key: navItem._key ?? String(itemIndex),
          link: navItem.link,
          displayName: undefined as string | undefined,
        })),
      }))
    : (content.items ?? []).map((group, groupIndex) => ({
        key: group.id ?? String(groupIndex),
        heading: group.title,
        items: (group.items ?? []).map((navItem, itemIndex) => ({
          key: navItem.id ?? String(itemIndex),
          link: navItem.link,
          displayName: navItem.displayName,
        })),
      }));

  if (groups.length === 0) return null;

  return (
    <Box paddingBottom={{ initial: '10', xl: '16' }} paddingTop={{ initial: '10', xl: '16' }}>
      <Grid columns={{ initial: '1', md: '2' }} gap="10" gridTemplateColumns={{ lg: 'repeat(auto-fit, minmax(0, 1fr))' }}>
        {groups.map(group => {
          const items = group.items;
          if (items.length === 0) return null;

          return (
            <Grid.Item key={group.key}>
              {group.heading && (
                <Heading size="XS" as="h2">
                  {group.heading}
                </Heading>
              )}
              <Box paddingTop="6">
                <Stack as="ul" gap="3">
                  {items.map(navItem => {
                    const link = linkWithUrl(navItem.link);
                    if (!link) return null;
                    return (
                      <Stack.Item as="li" key={navItem.key}>
                        <NavLink href={link.href} target={link.target} variant="tertiary">
                          {link.text || navItem.displayName || ''}
                        </NavLink>
                      </Stack.Item>
                    );
                  })}
                </Stack>
              </Box>
            </Grid.Item>
          );
        })}
      </Grid>
    </Box>
  );
};

export default FooterMenu;
